<template>
	<div class="game-container" ref="game-container" />
	<RouterView />
</template>

<script lang="ts" setup>
import { QuestInProgressStates } from "@/game/entities/config/quests/quest_states.ts";
import { createPhaserGame } from "@/game/PhaserGame";
import useEmitter, {
	EmitterEvents,
	type TileRepositoryUpgradePayload,
} from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker";
import router from "@/router";
import {
	markRaw,
	onBeforeUnmount,
	onMounted,
	ref,
	useTemplateRef,
	watch,
} from "vue";
import { RouterView } from "vue-router";

import ChestRepository from "@/game/ChestRepository";
import {
	tilerepoAllFishingBoatsFrameNames,
	tileRepoArenaFrameNames,
	tileRepoArmoryFrameNames,
	tileRepoBakeryFrameNames,
	tileRepoBlacksmithFrameNames,
	tilerepoCobbledEntityFrameNames,
	tilerepoForestFrameNames,
	tilerepoGoldMinesFrameNames,
	tilerepoGravelEntityFrameNames,
	tileRepoGuardTowerFrameNames,
	tileRepoHealersHutFrameNames,
	tilerepoLumberMillsFrameNames,
	tileRepoMarketplaceFrameNames,
	tileRepoMediumHousesFrameNames,
	tileRepoScoutTowerFrameNames,
	tileRepoUniversityFrameNames,
	tilerepoWaterFrameNames,
	tileRepoWheatFarmsFrameNames,
} from "@/game/entities/config";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { ResourceType } from "@/game/entities/config/resources";
import QuestRepository from "@/game/QuestRepository";
import { TileToastType } from "@/game/ToastMessage";
import useMapActions from "@/hooks/useMapActions";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import useSocketService from "@/hooks/useSocketService";
import useTileRepository from "@/hooks/useTileRepository";
import GameError, { GameErrorType } from "@/libs/GameError";
import useQuickToolStore from "@/store/useQuickToolStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettingsStore from "@/store/useSettingsStore";
import useSiegeSystemStore from "@/store/useSiegeSystemStore";
import useUserStore from "@/store/useUserStore";
import type { TileCoordinates, TileIndex } from "@/types/tile";
import { RouteNames } from "@/types/ui";
import { useDebounceFn } from "@vueuse/core";
import { storeToRefs } from "pinia";

const ticker = markRaw(useGameTicker());
const emitter = markRaw(useEmitter());
const tilerepo = markRaw(useTileRepository());
const socketService = markRaw(useSocketService());
const mapActions = markRaw(useMapActions());
const questrepo = markRaw(QuestRepository.getInstance());
const chestrepo = markRaw(ChestRepository.getInstance());

const { setPhaserReady } = useRuntimeStore();
const { increase } = useResourcesStore();
const { setExploredTiles } = useUserStore();
const { showGameSavedMessage } = storeToRefs(useSettingsStore());
const { active: isQtActive } = storeToRefs(useQuickToolStore());
const { firstClick } = storeToRefs(useRuntimeStore());
const { mainEnemyTileIndex } = storeToRefs(useSiegeSystemStore());

const containerRef = useTemplateRef<HTMLDivElement>("game-container");
const showPopup = ref<boolean>(false);
const saveErrorConsecutiveCount = ref<number>(0);

const onPhaserTileDetailOpen = ({ x, y }: TileCoordinates): void => {
	if (isQtActive.value || !firstClick.value) return;
	router.push({
		name: RouteNames.TILE,
		params: { x, y },
	});
};

const onPhaserRedirect = (): void => {
	router.push({ name: RouteNames.LAYOUT_VIEW });
};

const onPhaserSceneReady = (): void => {
	setPhaserReady(true);
};

const onTileRepositoryUpgrade = useDebounceFn(
	({ frameName }: TileRepositoryUpgradePayload): void => {
		if (!frameName) return;
		// TODO: Tu treba doplnit spravnu akciu / akcie.
		const questState = questrepo.manageQuestState(null, frameName);
		questrepo.manageQuestState(
			QuestObjectiveActions.BUILD_BUILDING,
			frameName,
		);
		questrepo.manageQuestState(
			QuestObjectiveActions.CHANGE_TERRAIN,
			frameName,
		);
		questrepo.manageQuestState(
			QuestObjectiveActions.HAVE_TERRAIN,
			frameName,
		);
		questrepo.manageQuestState(QuestObjectiveActions.HAVE_FULL_SETTLEMENTS);
		questrepo.manageQuestState(
			QuestObjectiveActions.HAVE_SETTLEMENTS_ON_LEVEL,
		);
		questrepo.manageQuestState(
			QuestObjectiveActions.INCREASE_RESOURCE_INCOME,
		);
		questrepo.manageQuestState(QuestObjectiveActions.HAVE_STORAGE);
		if (questState.includes(QuestInProgressStates.FINISHED))
			showPopup.value = true;
	},
	250,
);

const onPhaserTileExplored = (coordinates: TileCoordinates): void => {
	const tile = tilerepo.getTileEntityByTileCoordinates(coordinates);

	if (!chestrepo.initialized) {
		chestrepo.initialize(tilerepo);
	}

	chestrepo.exploreTile(coordinates);

	const chestOnExplored = chestrepo.getChestByCoordinates(coordinates);
	if (chestOnExplored && chestOnExplored.id) {
		chestrepo.claimChest(chestOnExplored.id);
	}

	// Define enrichable quest action checks
	const questActions = [
		{
			condition: (): boolean =>
				!!tile &&
				(tilerepoGoldMinesFrameNames.has(tile.frameName) ||
					tilerepoWaterFrameNames.has(tile.frameName) ||
					tileRepoWheatFarmsFrameNames.has(tile.frameName) ||
					tilerepoGravelEntityFrameNames.has(tile.frameName) ||
					tilerepoLumberMillsFrameNames.has(tile.frameName) ||
					tileRepoBakeryFrameNames.has(tile.frameName) ||
					tileRepoBlacksmithFrameNames.has(tile.frameName) ||
					tileRepoMediumHousesFrameNames.has(tile.frameName) ||
					tileRepoMarketplaceFrameNames.has(tile.frameName) ||
					tileRepoHealersHutFrameNames.has(tile.frameName) ||
					tilerepoCobbledEntityFrameNames.has(tile.frameName) ||
					tileRepoUniversityFrameNames.has(tile.frameName) ||
					tileRepoArmoryFrameNames.has(tile.frameName) ||
					tileRepoScoutTowerFrameNames.has(tile.frameName) ||
					tileRepoGuardTowerFrameNames.has(tile.frameName) ||
					tileRepoArenaFrameNames.has(tile.frameName) ||
					tilerepoForestFrameNames.has(tile.frameName) ||
					tilerepoAllFishingBoatsFrameNames.has(tile.frameName)),
			action: QuestObjectiveActions.HAVE_TERRAIN,
		},
		{
			condition: (): boolean => true,
			action: QuestObjectiveActions.CLEAR_TILES,
		},
	];

	let questState: Array<QuestInProgressStates | void> = [];

	for (const { condition, action } of questActions) {
		if (condition()) {
			questState = questrepo.manageQuestState(
				action,
				tile?.frameName ?? null,
			);
			if (questState.includes(QuestInProgressStates.FINISHED)) {
				showPopup.value = true;
			}
		}
	}
};

onMounted((): void => {
	if (!containerRef.value) throw new Error("Container not found");
	if (window.__PHASER_GAME__) throw new Error("Game already initialized");

	window.__PHASER_GAME__ = createPhaserGame(containerRef.value);

	ticker.addHandler("999:save", (): void => {
		increase(ResourceType.TICKS_TOTAL, 1, false);
		setExploredTiles(tilerepo.exploredTileCount);

		// non-blocking save
		socketService
			.emitSaveRequest()
			.then((): void => {
				const serializedRoadGraph = tilerepo.getSerializedRoadGraph();
				//TODO doplnit catch
				tilerepo.saveRoadGraphToDB(serializedRoadGraph).then(() => {
					saveErrorConsecutiveCount.value = 0;
					// save message
					if (showGameSavedMessage.value) {
						// show game saved message
						emitter.emit(EmitterEvents.PhaserTextMessage, {
							text: mapActions.isActionQueueEmpty()
								? "ui.game_saved"
								: "ui.game_partially_saved",
							type: TileToastType.SUCCESS,
						});
					}
				});
			})
			.catch((error: GameError | Error): void => {
				saveErrorConsecutiveCount.value += 1;
				if (saveErrorConsecutiveCount.value > 2) {
					const recoverable =
						!socketService.isSocketActive ||
						!navigator.onLine ||
						(error instanceof GameError &&
							[
								GameErrorType.NETWORK_DISCONNECT_DURING_REQUEST,
								GameErrorType.BE_TIMEOUT_ERROR,
							].includes(error.type));
					emitter.emit(
						EmitterEvents.GameShowConnectionErrorPopup,
						recoverable ? { recoverable: true } : undefined,
					);
				}
				// error save message
				emitter.emit(EmitterEvents.PhaserTextMessage, {
					text: "ui.save_error",
					type: TileToastType.WARNING,
				});

				if (error instanceof GameError)
					throw new GameError(error.type, error.message);

				if (error instanceof Error) throw new Error(error.message);

				console.error(error);
			});
	});
	mapActions.init();

	// Emitter for handling game interaction events
	emitter.on(EmitterEvents.PhaserTileDetailOpen, onPhaserTileDetailOpen);
	emitter.on(EmitterEvents.PhaserRedirect, onPhaserRedirect);
	emitter.on(EmitterEvents.PhaserSceneReady, onPhaserSceneReady);
	emitter.on(EmitterEvents.TileRepositoryUpgrade, onTileRepositoryUpgrade);
	emitter.on(EmitterEvents.PhaserTileExplored, onPhaserTileExplored);
});

onBeforeUnmount((): void => {
	emitter.off(EmitterEvents.PhaserTileDetailOpen, onPhaserTileDetailOpen);
	emitter.off(EmitterEvents.PhaserRedirect, onPhaserRedirect);
	emitter.off(EmitterEvents.PhaserSceneReady, onPhaserSceneReady);
	emitter.off(EmitterEvents.TileRepositoryUpgrade, onTileRepositoryUpgrade);
	emitter.off(EmitterEvents.PhaserTileExplored, onPhaserTileExplored);

	if (!window.__PHASER_GAME__) return;

	window.__PHASER_GAME__.destroy(true);
	window.__PHASER_GAME__ = null;
});

watch(
	(): TileIndex | null => mainEnemyTileIndex.value,
	(newValue: TileIndex | null, oldValue: TileIndex | null): void => {
		const tileIndex = newValue ?? oldValue;
		if (tileIndex === null) return;

		useSiegeSystemRepository().rerenderBattlefieldLabelByTileIndex(
			tileIndex,
		);
	},
);
</script>

<style lang="scss" scoped>
.game-container {
	position: absolute;
	top: 0;
	left: 0;
}

.toggle {
	position: absolute;
	top: 5rem;
	left: 50%;
	transform: translateX(-50%);
	z-index: 99;
	padding: 0.125rem 0.25rem;
}

.popup {
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 100;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
	border-radius: 5px;
	background: #4b2e1a;
	padding: 20px;
	color: white;
}

.popup button {
	cursor: pointer;
	margin-top: 10px;
	border: none;
	background: white;
	padding: 5px 10px;
	color: black;
}
</style>
